(() => {
  const productSelector = '[data-naxos-product], [data-naxos-product-page], .naxos-recommendations-card, .naxos-favorites-product';

  function visibleRect(element) {
    if (!element?.isConnected) return null;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) return null;
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
      rect.top < window.innerHeight && rect.left < window.innerWidth ? rect : null;
  }

  function motionEnabled() {
    return document.body.dataset.naxosCartAnimation !== 'false' &&
      !document.documentElement.classList.contains('naxos-a11y-reduce-motion') &&
      !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches &&
      typeof Element.prototype.animate === 'function';
  }

  function animateCartArrival(destination) {
    const rect = visibleRect(destination);
    if (!rect || !motionEnabled()) return;
    const ripple = document.createElement('span');
    ripple.className = 'naxos-cart-arrival-ripple';
    ripple.setAttribute('aria-hidden', 'true');
    Object.assign(ripple.style, {
      position: 'fixed', left: rect.left + 'px', top: rect.top + 'px',
      width: rect.width + 'px', height: rect.height + 'px', borderRadius: '50%',
      border: '2px solid #61afea', boxShadow: '0 0 22px rgba(0, 84, 159, .3)',
      boxSizing: 'border-box', pointerEvents: 'none', zIndex: '2147483646',
    });
    document.body.append(ripple);
    try {
      const wave = ripple.animate([
        { transform: 'scale(.8)', opacity: .8 },
        { transform: 'scale(1.65)', opacity: 0 },
      ], { duration: 550, easing: 'cubic-bezier(.16, 1, .3, 1)' });
      wave.finished.then(() => ripple.remove(), () => ripple.remove());
      // The individual scale property preserves any positioning transform on the cart.
      destination.animate([
        { scale: '1' }, { scale: '1.14', offset: .35 },
        { scale: '.97', offset: .7 }, { scale: '1' },
      ], { duration: 500, easing: 'ease-in-out' });
    } catch (error) {
      ripple.remove();
    }
  }

  async function animateProductToCart(form) {
    document.dispatchEvent(new CustomEvent('naxos:cart:reveal'));
    if (!(form instanceof Element) || !motionEnabled()) return;

    const product = form.closest(productSelector);
    const image = product?.querySelector('[data-naxos-gallery-slide].is-active img') ||
      product?.querySelector('.naxos-products__cover--primary, .naxos-recommendations-card__cover--primary, img');
    const sourceUrl = image?.currentSrc || image?.src;
    if (!sourceUrl) return;

    const cover = document.createElement('img');
    cover.alt = '';
    cover.className = 'naxos-cart-flying-image';
    cover.loading = 'eager';
    const imageReady = new Promise((resolve) => {
      cover.onload = () => resolve(true);
      cover.onerror = () => resolve(false);
    });
    cover.src = sourceUrl;
    if (!(cover.complete ? cover.naturalWidth > 0 : await imageReady)) return;
    if (typeof cover.decode === 'function') {
      await cover.decode().catch(() => {});
    }
    if (!form.isConnected || !motionEnabled()) return;

    document.dispatchEvent(new CustomEvent('naxos:cart:reveal'));
    await new Promise((resolve) => window.requestAnimationFrame(resolve));
    if (!form.isConnected || !motionEnabled()) return;

    const floatingCart = document.querySelector('[data-naxos-action-dock] [data-naxos-cart-link]');
    const destination = window.matchMedia('(max-width: 749px)').matches && visibleRect(floatingCart) ? floatingCart :
      Array.from(document.querySelectorAll('[data-naxos-cart-link]')).find(visibleRect);
    const destinationRect = visibleRect(destination);
    // On small screens the cover may be above the viewport; launch its image from the button instead.
    const sourceRect = visibleRect(image) || visibleRect(form.querySelector('button[type="submit"]'));
    if (!sourceRect || !destinationRect) return;

    const maxSize = window.innerWidth < 600 ? 144 : 184;
    const aspectRatio = cover.naturalWidth / cover.naturalHeight;
    const width = aspectRatio >= 1 ? maxSize : maxSize * aspectRatio;
    const height = aspectRatio >= 1 ? maxSize / aspectRatio : maxSize;
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    // Keep the lifted cover visible even when its original image is partly outside the viewport.
    const x = clamp(sourceRect.left + sourceRect.width / 2, width / 2 + 12, window.innerWidth - width / 2 - 12);
    const y = clamp(sourceRect.top + sourceRect.height / 2, height / 2 + 12, window.innerHeight - height / 2 - 12);
    const endX = destinationRect.left + destinationRect.width / 2;
    const endY = destinationRect.top + destinationRect.height / 2;
    const travelX = endX - x;
    const travelY = endY - y;
    const lift = Math.min(160, Math.max(70, Math.hypot(travelX, travelY) * .24));
    const control1 = { x: x + travelX * .18, y: Math.max(height / 2 + 12, y - lift) };
    const control2 = { x: x + travelX * .82, y: clamp(endY - lift * .65, height / 2 + 12, window.innerHeight - height / 2 - 12) };
    const duration = 1800;
    const flyer = document.createElement('div');
    flyer.className = 'naxos-cart-flight';
    flyer.setAttribute('aria-hidden', 'true');
    Object.assign(flyer.style, {
      position: 'fixed', left: (x - width / 2) + 'px', top: (y - height / 2) + 'px',
      width: width + 'px', height: height + 'px', pointerEvents: 'none',
      zIndex: '2147483647', transformOrigin: 'center', willChange: 'transform, opacity',
    });
    Object.assign(cover.style, {
      display: 'block', width: '100%', height: '100%', maxWidth: 'none', objectFit: 'contain',
      padding: '8px', boxSizing: 'border-box', background: 'rgba(255, 255, 255, .97)',
      border: '1px solid rgba(255, 255, 255, .95)', borderRadius: '16%',
      boxShadow: '0 20px 44px rgba(0, 28, 61, .35), 0 0 26px rgba(83, 170, 237, .45)',
      transformOrigin: 'center',
    });
    flyer.append(cover);
    document.body.append(flyer);

    await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
    if (!form.isConnected || !motionEnabled()) {
      flyer.remove();
      return;
    }

    // Sample a continuous Bezier arc: the cover flows along the curve without cornering at waypoints.
    const keyframes = Array.from({ length: 61 }, (_, index) => {
      const t = index / 60;
      const inverse = 1 - t;
      const px = inverse ** 3 * x + 3 * inverse ** 2 * t * control1.x + 3 * inverse * t ** 2 * control2.x + t ** 3 * endX;
      const py = inverse ** 3 * y + 3 * inverse ** 2 * t * control1.y + 3 * inverse * t ** 2 * control2.y + t ** 3 * endY;
      // Stay large for most of the flight, then gently pour into the cart.
      const shrink = clamp((t - .38) / .62, 0, 1);
      const scale = 1 + .08 * Math.sin(t * Math.PI) - .86 * shrink * shrink * (3 - 2 * shrink);
      return {
        offset: t,
        transform: 'translate(' + (px - x) + 'px, ' + (py - y) + 'px) scale(' + scale + ')',
        opacity: t < .92 ? 1 : (1 - t) / .08,
      };
    });

    try {
      cover.animate([
        { transform: 'scale(1, 1) rotate(0deg)', borderRadius: '16%' },
        { transform: 'scale(1.07, .94) rotate(-3deg)', borderRadius: '22% 16% 23% 17%', offset: .18 },
        { transform: 'scale(.96, 1.06) rotate(3deg)', borderRadius: '16% 24% 17% 22%', offset: .48 },
        { transform: 'scale(1.05, .95) rotate(-2deg)', borderRadius: '24% 18% 22% 16%', offset: .76 },
        { transform: 'scale(.92, 1.08) rotate(0deg)', borderRadius: '32%' },
      ], { duration, easing: 'ease-in-out', fill: 'forwards' });
      const flight = flyer.animate(keyframes, {
        duration, easing: 'cubic-bezier(.42, 0, .22, 1)', fill: 'forwards',
      });
      flight.finished.then(() => {
        flyer.remove();
        animateCartArrival(destination);
      }, () => flyer.remove());
    } catch (error) {
      flyer.remove();
    }

  }

  document.addEventListener('cart:updated', (event) => {
    animateProductToCart(event.detail?.source);
  });

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'product-form-component' && !event.detail.data.didError) {
      document.dispatchEvent(new CustomEvent('naxos:cart:reveal'));
    }
  });

  // Recommendation and saved-product forms retain their normal POST fallback without JavaScript.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) ||
      !form.matches('[data-naxos-quick-add-form]') || typeof window.fetch !== 'function') return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    if (!button || button.disabled) return;
    const originalText = button.textContent;
    const formData = new FormData(form);
    const action = form.action.endsWith('.js') ? form.action : form.action + '.js';
    let status = form.querySelector('[data-naxos-quick-add-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.naxosQuickAddStatus = '';
      status.setAttribute('role', 'status');
      form.append(status);
    }
    status.classList.toggle('visually-hidden', Boolean(form.closest('.naxos-product-related')));
    status.setAttribute('aria-live', 'polite');
    status.textContent = '';
    form.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.textContent = window.NaxosText("products.product.adding_to_cart");

    try {
      const response = await window.fetch(action, {
        method: 'POST', body: formData, headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Add to cart failed');
      await response.json();
      button.textContent = window.NaxosText("actions.added");
      status.textContent = window.NaxosText("naxos_ui.cart_added");
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: form } }));
      window.setTimeout(() => {
        button.disabled = false;
        form.removeAttribute('aria-busy');
        button.textContent = originalText;
      }, 1400);
    } catch (error) {
      status.classList.remove('visually-hidden');
      status.setAttribute('aria-live', 'assertive');
      status.textContent = window.NaxosText("naxos_ui.cart_error");
      button.disabled = false;
      form.removeAttribute('aria-busy');
      button.textContent = originalText;
    }
  });
})();
